<template>
  <div
    v-if="shortTextAd"
    :class="['short-text-ad-cont', globalClassAttributes]"
  >
    <div
      class="text-cont"
      :style="styleAttributes"
    >
      <div
        v-if="shortTextAd?.attributes?.body"
        :class="['text', {'text-vert': shortTextAd?.ctas?.attributes?.linkLayout === 'vertical',
        }] "
        v-html="sanitize(shortTextAd?.attributes?.body, cleanStyles)"
      />

      <span
        v-if="shortTextAd?.ctas"
        :class="[
          'ctas-cont',
          {
            'link-vert': shortTextAd.ctas.attributes?.linkLayout === 'vertical',
          },
        ]"
      >
        <template
          v-for="(link, index) in (shortTextAd?.ctas?.children || [])"
          :key="index"
        >
          <a
            v-if="typeof link?.url !== 'undefined' && typeof link?.text !== 'undefined'"
            :class="ctasClass"
            :href="link?.url"
            :alt="link?.alt ?? ''"
          >{{ link?.text }}
          </a>
        </template>
      </span>
    </div>
  </div>
</template>

<script setup>
import { sanitize } from '@vue-util/sanitize';
import {
  getClassAttributes,
  getStyleAttributes,
} from '../../helpers/getAttributes';
import cleanStyles from '../../helpers/getCleanStyles';

const props = defineProps({
  shortTextAd: {
    type: Object,
    default: () => {},
  },
});

const globalClassAttributes = getClassAttributes(props.shortTextAd?.attributes);
const styleAttributes = getStyleAttributes(props.shortTextAd?.attributes);

const linkStyle = {
  primaryButton: 'primary button',
  secondaryButton: 'secondary button',
  underlinedText: 'link-upper-med',
};
const flexMapping = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
};
const ctasClass = linkStyle[props.shortTextAd?.ctas?.attributes?.linkStyle] || 'link-med';
const fontColor = props.shortTextAd?.attributes?.fontColor
  ? props.shortTextAd.attributes.fontColor
  : 'inherit';
const alignment = props.shortTextAd?.attributes?.alignment
  ? props.shortTextAd.attributes.alignment
  : 'left';
const flexAlignment = flexMapping[props.shortTextAd?.attributes?.alignment] || flexMapping.left;
</script>

<style lang="scss" scoped>
@import '~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss';

.text-cont {
  display: flex;
  align-items: last baseline;
  flex-wrap: wrap;
  justify-content: center;
  word-wrap: break-word;
  text-align: center;
  width: 100%;
  color: v-bind(fontColor);
  padding: rem-calc(16 20);
  @include breakpoint(large) {
    padding: rem-calc(16 80);
  }

}

.short-text-ad-cont :deep(*){
  .text *{
    color: v-bind(fontColor);
    a {
      text-decoration-thickness: rem-calc(1px);
      text-underline-offset: rem-calc(2px);
    }
  }
}
.text {
  display: inline-block;
  vertical-align: middle;
}
.ctas-cont {
  a {
    display: inline-flex;
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import './../../scss/globalAttributes.mcom.scss';

.text-cont {
  text-align: center;
}
.text {
  @include margin-direction('right', map-get($spacer, 'xxs'));
  :deep(*) {
    a:hover {
      text-decoration-thickness: rem-calc(2px);
      opacity: 1;
    }
  }
}
.ctas-cont {
  a {
    @include margin-direction('right', map-get($spacer, 'xs'));
    margin: rem-calc(4 0);
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@import './../../scss/globalAttributes.bcom.scss';
.text-cont {
  text-align: v-bind(alignment);
  justify-content: v-bind(alignment);
}
.text {
    @include margin-direction('right', map-get($spacer, 's'));
    :deep(*) {
      a:hover {
        opacity: 0.65;
      }
    }
}
.ctas-cont {
  a {
    @include margin-direction('right', map-get($spacer, 's'));
    @include margin-direction('top', map-get($spacer, 'xxs'));
    @include margin-direction('bottom', map-get($spacer, 'xxs'));
  }
  &.link-vert {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: v-bind(flexAlignment);
    a {
      @include margin-direction('right', 0);
    }
    a:first-of-type {
      @include margin-direction('top', map-get($spacer, 'xs'));
    }
    a:last-of-type {
      @include margin-direction('bottom', 0);
    }
  }
}

.text {
  &.text-vert {
    width: 100%;
    @include margin-direction('right', 0);
  }
}
</style>
